import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { ChevronLeft, ChevronRight, Zap, Cpu, Rocket, Clock, CheckCircle2, Target } from 'lucide-react'

const QRLanding = () => {
  const { t } = useTranslation()
  const [currentSlide, setCurrentSlide] = useState(0)
  const [direction, setDirection] = useState(1)

  const slides = [
    {
      id: 1,
      icon: <Zap className="w-12 h-12 text-purple-400" />,
      badge: t('qrLanding.slide1Badge', 'n8n Automation Experts'),
      title: t('qrLanding.slide1Title', 'Save 20+ Hours Weekly'),
      description: t('qrLanding.slide1Desc', 'We build smart workflows that eliminate repetitive tasks and connect all your tools automatically'),
      stats: [
        { icon: <Clock className="w-4 h-4" />, text: t('qrLanding.slide1Stat1', '20+ Hours Saved') },
        { icon: <CheckCircle2 className="w-4 h-4" />, text: t('qrLanding.slide1Stat2', '50+ Workflows') },
        { icon: <Target className="w-4 h-4" />, text: t('qrLanding.slide1Stat3', '95% Client Satisfaction') },
      ],
    },
    {
      id: 2,
      icon: <Cpu className="w-12 h-12 text-purple-400" />,
      badge: t('qrLanding.slide2Badge', 'How We Help'),
      title: t('qrLanding.slide2Title', 'Automate Everything'),
      description: t('qrLanding.slide2Desc', 'We connect your tools, sync data between systems, and build workflows that run 24/7 without your intervention'),
      features: [
        t('qrLanding.slide2Feature1', 'CRM sync & data enrichment'),
        t('qrLanding.slide2Feature2', 'Lead generation automation'),
        t('qrLanding.slide2Feature3', 'Automatic reports & notifications'),
        t('qrLanding.slide2Feature4', 'Save time & grow revenue'),
      ],
    },
    {
      id: 3,
      icon: <Rocket className="w-12 h-12 text-purple-400" />,
      badge: t('qrLanding.slide3Badge', 'Start Free'),
      title: t('qrLanding.slide3Title', 'Book Your Free Call'),
      description: t('qrLanding.slide3Desc', 'In 30 minutes we learn about your business and create a clear automation plan. No commitment.'),
      isFinal: true,
    },
  ]

  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      setDirection(1)
      setCurrentSlide((prev) => prev + 1)
    }
  }

  const prevSlide = () => {
    if (currentSlide > 0) {
      setDirection(-1)
      setCurrentSlide((prev) => prev - 1)
    }
  }

  const goToSlide = (index) => {
    setDirection(index > currentSlide ? 1 : -1)
    setCurrentSlide(index)
  }

  const skipToEnd = () => {
    window.location.href = 'https://nodatx.com'
  }

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? -300 : 300,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction) => ({
      x: direction > 0 ? 300 : -300,
      opacity: 0,
    }),
  }

  const currentSlideData = slides[currentSlide]

  return (
    <div className="min-h-screen bg-brand-dark flex flex-col relative overflow-hidden" dir="ltr">
      {/* Hero-style Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 bg-[#0a0a0f]"></div>
        <div className="absolute top-1/4 right-1/4 w-[500px] h-[500px] bg-purple-600/25 rounded-full blur-[120px] animate-pulse-slow"></div>
        <div className="absolute bottom-0 left-1/4 w-[600px] h-[600px] bg-purple-500/10 rounded-full blur-[150px] animate-pulse-slow" style={{ animationDelay: '2s' }}></div>
        <div className="absolute top-1/2 right-1/2 translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-purple-500/20 rounded-full blur-[100px]"></div>
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff03_1px,transparent_1px),linear-gradient(to_bottom,#ffffff03_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]"></div>
      </div>

      {/* Top: Logo + Skip */}
      <div className="relative z-10 flex items-center justify-between px-6 pt-6">
        {!currentSlideData.isFinal && (
          <button
            onClick={skipToEnd}
            className="px-5 py-2 text-gray-400 hover:text-white font-medium transition-colors text-sm bg-white/5 hover:bg-white/10 border border-white/10 rounded-full"
            dir="ltr"
          >
            {t('qrLanding.skip', 'Skip')}
          </button>
        )}
        <div className="flex items-center gap-3">
          <div className="w-12 h-12">
            <img src="/nodatx.png" alt="Nodatx" className="w-full h-full object-contain" />
          </div>
          <span className="text-xl font-bold text-white">Noda<span className="text-purple-400">tx</span></span>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-6 relative z-10 py-4">
        <div className="w-full max-w-lg">
          {/* Slide Container */}
          <div className="relative min-h-[420px] flex items-center">
            <AnimatePresence initial={false} custom={direction} mode="wait">
              <motion.div
                key={currentSlide}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  x: { type: 'spring', stiffness: 300, damping: 30 },
                  opacity: { duration: 0.2 },
                }}
                className="text-center absolute inset-0 flex flex-col items-center justify-center py-8"
              >
                {/* Icon */}
                <motion.div
                  initial={{ scale: 0.7, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.1, type: 'spring', stiffness: 200 }}
                  className="mb-5"
                >
                  <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-purple-500/10 backdrop-blur-sm border border-purple-500/20 shadow-lg">
                    {currentSlideData.icon}
                  </div>
                </motion.div>

                {/* Badge */}
                <motion.div
                  initial={{ y: 10, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="mb-3"
                >
                  <span className="inline-block px-4 py-1.5 bg-purple-500/10 border border-purple-500/30 rounded-full text-purple-400 text-sm font-medium">
                    {currentSlideData.badge}
                  </span>
                </motion.div>

                {/* Title */}
                <motion.h1
                  initial={{ y: 10, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="text-2xl md:text-3xl font-bold mb-3 text-white text-right"
                  dir="ltr"
                >
                  {currentSlideData.title}
                </motion.h1>

                {/* Description */}
                <motion.p
                  initial={{ y: 10, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="text-sm md:text-base text-gray-400 max-w-sm mx-auto leading-relaxed mb-5 text-right"
                  dir="ltr"
                >
                  {currentSlideData.description}
                </motion.p>

                {/* Stats (Slide 1) */}
                {currentSlideData.stats && (
                  <motion.div
                    initial={{ y: 10, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    className="flex flex-wrap justify-center gap-2"
                  >
                    {currentSlideData.stats.map((stat, i) => (
                      <div key={i} className="flex items-center gap-2 px-3 py-1.5 bg-white/5 border border-white/10 rounded-full">
                        <span className="text-purple-400">{stat.icon}</span>
                        <span className="text-gray-300 text-xs font-medium whitespace-nowrap" dir="ltr">{stat.text}</span>
                      </div>
                    ))}
                  </motion.div>
                )}

                {/* Features (Slide 2) */}
                {currentSlideData.features && (
                  <motion.div
                    initial={{ y: 10, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    className="flex flex-col gap-2 w-full max-w-xs mx-auto"
                  >
                    {currentSlideData.features.map((feature, i) => (
                      <div key={i} className="flex items-center gap-3 text-left" dir="ltr">
                        <CheckCircle2 className="w-4 h-4 text-purple-400 flex-shrink-0" />
                        <span className="text-gray-300 text-sm">{feature}</span>
                      </div>
                    ))}
                  </motion.div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Slide Indicators */}
          <div className="flex justify-center gap-3 mt-4" dir="rtl">
            {slides.map((slide, index) => (
              <button
                key={slide.id}
                onClick={() => goToSlide(slides.length - 1 - index)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === currentSlide
                    ? 'w-8 bg-purple-400'
                    : 'w-2 bg-white/20 hover:bg-white/40'
                }`}
                aria-label={`Slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Controls */}
      <div className="px-6 pb-8 md:pb-12 relative z-10">
        <div className="max-w-lg mx-auto">
          {/* Navigation Buttons */}
          <div className="flex items-center justify-between gap-4">
            {/* Prev Button - LEFT */}
            {currentSlide > 0 ? (
              <button
                onClick={prevSlide}
                className="p-3.5 rounded-full border border-white/15 hover:border-purple-400 hover:bg-purple-400/10 text-white transition-all"
                aria-label={t('qrLanding.prev', 'Previous')}
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            ) : (
              <div className="w-12"></div>
            )}

            {/* Next or Final Button - RIGHT */}
            {currentSlideData.isFinal ? (
              <a
                href="https://nodatx.com"
                className="flex-1 flex items-center justify-center gap-2 px-8 py-4 bg-purple-600 text-white font-bold rounded-xl hover:shadow-[0_0_40px_-10px_rgba(139,92,246,0.5)] transition-all text-lg"
              >
                <Rocket className="w-5 h-5" />
                <span>{t('qrLanding.getStarted', 'Get Started')}</span>
              </a>
            ) : (
              <button
                onClick={nextSlide}
                className="flex items-center gap-2 px-8 py-4 bg-purple-600 text-white font-bold rounded-xl hover:shadow-[0_0_40px_-10px_rgba(139,92,246,0.5)] transition-all text-lg"
              >
                <ChevronLeft className="w-5 h-5" />
                <span>{t('qrLanding.next', 'Next')}</span>
              </button>
            )}
          </div>


        </div>
      </div>
    </div>
  )
}

export default QRLanding
